import { useEffect, useState } from 'react';
import { NavLink, useLocation, useNavigate } from 'react-router-dom';
import { useAuth } from '../auth.jsx';
import { useTheme } from '../theme.jsx';
import { api } from '../api';

const Icon = ({ d }) => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"
    strokeLinecap="round" strokeLinejoin="round"><path d={d} /></svg>
);

const ICONS = {
  dashboard: 'M3 12l9-9 9 9M5 10v10h5v-6h4v6h5V10',
  employees: 'M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2M9 11a4 4 0 1 0 0-8 4 4 0 0 0 0 8zM23 21v-2a4 4 0 0 0-3-3.87M16 3.13a4 4 0 0 1 0 7.75',
  attendance: 'M8 2v4M16 2v4M3 10h18M5 4h14a2 2 0 0 1 2 2v14a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V6a2 2 0 0 1 2-2z',
  payroll: 'M12 1v22M17 5H9.5a3.5 3.5 0 0 0 0 7h5a3.5 3.5 0 0 1 0 7H6',
  advances: 'M21 12V7H5a2 2 0 0 1 0-4h14v4M3 5v14a2 2 0 0 0 2 2h16v-5M18 12a2 2 0 0 0 0 4h4v-4h-4z',
  incidents: 'M10.29 3.86 1.82 18a2 2 0 0 0 1.71 3h16.94a2 2 0 0 0 1.71-3L13.71 3.86a2 2 0 0 0-3.42 0zM12 9v4M12 17h.01',
  reports: 'M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8zM14 2v6h6M16 13H8M16 17H8M10 9H8',
  users: 'M12 15a4 4 0 1 0 0-8 4 4 0 0 0 0 8zm0 0v6m-9-3h18',
  organization: 'M3 21h18M5 21V7l7-4 7 4v14M9 9h1M9 13h1M14 9h1M14 13h1M9 21v-4h6v4',
  bell: 'M18 8A6 6 0 0 0 6 8c0 7-3 9-3 9h18s-3-2-3-9M13.73 21a2 2 0 0 1-3.46 0',
  back: 'M19 12H5M12 19l-7-7 7-7',
  sun: 'M12 17a5 5 0 1 0 0-10 5 5 0 0 0 0 10zM12 1v2M12 21v2M4.22 4.22l1.42 1.42M18.36 18.36l1.42 1.42M1 12h2M21 12h2M4.22 19.78l1.42-1.42M18.36 5.64l1.42-1.42',
  moon: 'M21 12.79A9 9 0 1 1 11.21 3 7 7 0 0 0 21 12.79z',
};

const ROLE_LABEL = { MASTER_ADMIN: 'Master Admin', HR_MANAGER: 'HR Manager', SUPERVISOR: 'Site Supervisor' };

export default function Layout({ children }) {
  const { user, logout, can } = useAuth();
  const { theme, toggle } = useTheme();
  const location = useLocation();
  const navigate = useNavigate();
  const [notifCount, setNotifCount] = useState(0);

  useEffect(() => {
    let live = true;
    const load = () => api('/notifications').then((n) => live && setNotifCount(n.length)).catch(() => {});
    load();
    const t = setInterval(load, 60000);
    return () => { live = false; clearInterval(t); };
  }, [location.pathname]);

  const nav = [
    { to: '/', label: 'Dashboard', icon: 'dashboard', show: true },
    { to: '/employees', label: 'Employees', icon: 'employees', show: can('employees.view') },
    { to: '/attendance', label: 'Attendance', icon: 'attendance', show: can('attendance.view') },
    { to: '/advances', label: 'Advances', icon: 'advances', show: can('advances.view') },
    { to: '/payroll', label: 'Payroll', icon: 'payroll', show: can('payroll.view') },
    { to: '/incidents', label: 'Incidents', icon: 'incidents', show: can('incidents.view') },
    { to: '/reports', label: 'Reports', icon: 'reports', show: can('reports.view') },
    { to: '/organization', label: 'Organization', icon: 'organization',
      show: can('sites.manage') || can('departments.manage') || can('employment_types.manage') },
    { to: '/users', label: 'User Access', icon: 'users', show: can('users.manage') },
  ].filter((n) => n.show);

  const titles = {
    '/': 'Dashboard', '/employees': 'Employee Management', '/attendance': 'Time & Attendance',
    '/advances': 'Advances & Loans', '/payroll': 'Payroll Management', '/incidents': 'Incidents & Warnings',
    '/reports': 'Reports', '/organization': 'Organization Setup', '/users': 'User Access Control', '/notifications': 'Notifications',
  };
  const title = titles[location.pathname]
    ?? (location.pathname.startsWith('/payroll') ? 'Payroll Management'
      : location.pathname.startsWith('/employees') ? 'Employee Profile' : 'RMG HR');

  return (
    <div className="shell">
      <aside id="sidebar">
        <div className="sidebar-logo">
          <div className="logo-icon">R</div>
          <div>
            <div className="logo-text">RMG HR</div>
            <div className="logo-sub">Workforce Management</div>
          </div>
        </div>
        <nav className="sidebar-nav">
          <div className="nav-section-label">Menu</div>
          {nav.map((n) => (
            <NavLink key={n.to} to={n.to} end={n.to === '/'}
              className={({ isActive }) => 'nav-item' + (isActive ? ' active' : '')}>
              <Icon d={ICONS[n.icon]} />
              <span>{n.label}</span>
            </NavLink>
          ))}
        </nav>
        <div className="sidebar-footer">
          <div className="user-card" onClick={logout} title="Sign out">
            <div className="user-avatar-sm">{user.name.split(' ').slice(0, 2).map((w) => w[0]).join('')}</div>
            <div className="user-info-sm">
              <div className="name">{user.name}</div>
              <div className="role">{ROLE_LABEL[user.role]} · Sign out</div>
            </div>
          </div>
        </div>
      </aside>

      <div id="main">
        <div className="topbar">
          {location.pathname !== '/' && (
            <button className="icon-btn" title="Go back"
              onClick={() => (window.history.state?.idx > 0 ? navigate(-1) : navigate('/'))}>
              <Icon d={ICONS.back} />
            </button>
          )}
          <div className="topbar-title">{title}</div>
          <div className="topbar-date">
            {new Date().toLocaleDateString('en-GB', { weekday: 'long', day: 'numeric', month: 'long', year: 'numeric' })}
          </div>
          <button className="icon-btn" onClick={toggle}
            title={theme === 'dark' ? 'Switch to light mode' : 'Switch to dark mode'}>
            <Icon d={theme === 'dark' ? ICONS.sun : ICONS.moon} />
          </button>
          <button className="icon-btn" onClick={() => navigate('/notifications')} title="Notifications">
            <Icon d={ICONS.bell} />
            {notifCount > 0 && <span className="notif-count">{notifCount > 9 ? '9+' : notifCount}</span>}
          </button>
        </div>
        <div className="page-content">{children}</div>
      </div>

      <nav className="mobile-nav">
        {nav.slice(0, 5).map((n) => (
          <NavLink key={n.to} to={n.to} end={n.to === '/'}
            className={({ isActive }) => 'mobile-nav-item' + (isActive ? ' active' : '')}>
            <Icon d={ICONS[n.icon]} />
            <span>{n.label}</span>
          </NavLink>
        ))}
      </nav>
    </div>
  );
}
